import http from "@/axios/http-common";
import ErrorAlert from "@/utility/ErrorAlert";

export interface RegisterRalanHarianDiagnosisItem {
  kode: string;
  deskripsi: string;
  urut: number | string | null;
  kasus: "B" | "L" | null;
  kasus_label: string;
}

export interface RegisterRalanHarianRow {
  no_register: string | number;
  tgl: string;
  jam: string;
  nrm: string;
  nik: string;
  nama: string;
  jk: string;
  umur: string;
  alamat: string;
  provinsi: string;
  kabupaten: string;
  alamat_wilayah: string;
  cara_bayar: string;
  no_sep: string;
  poli: string;
  jenis_kunjungan: string;
  asal_rujukan: string;
  jam_layanan: string;
  jam_layanan_at: string | null;
  waktu_tunggu_menit: number | null;
  waktu_tunggu_text: string;
  dpjp: string;
  diagnosis: RegisterRalanHarianDiagnosisItem[];
  prosedur: { kode: string; deskripsi: string }[];
  dx_text: string;
  kasus: "B" | "L" | null;
  kasus_text: string;
  tindakan_text: string;
  tindakan_kode_text: string;
  status: string;
  disposisi: string;
}

export interface RegisterRalanHarianSummary {
  periode: { start: string; end: string };
  total_kunjungan: number;
  pasien_unik: number;
  baru: number;
  lama: number;
  distribusi_cara_bayar: { label: string; jumlah: number }[];
  distribusi_poli: { label: string; jumlah: number }[];
  top_diagnosis: { kode: string; deskripsi: string; jumlah: number }[];
}

export interface RegisterRalanHarianResponse {
  rows: RegisterRalanHarianRow[];
  summary: RegisterRalanHarianSummary;
  params: Record<string, unknown>;
}

export interface RegisterRalanHarianFilter {
  tgl_start: string; // YYYY-MM-DD
  tgl_end: string; // YYYY-MM-DD
  unit?: string[];
  dr?: string[];
  asr?: string[];
  jenis_kunjungan?: "semua" | "baru" | "lama";
}

async function preview(
  filter: RegisterRalanHarianFilter
): Promise<RegisterRalanHarianResponse> {
  const res = await http
    .getDataParam(`/laporan/register-ralan-harian`, {
      params: {
        tgl_start: filter.tgl_start,
        tgl_end: filter.tgl_end,
        unit: filter.unit,
        dr: filter.dr,
        asr: filter.asr,
        jenis_kunjungan: filter.jenis_kunjungan
      }
    })
    .catch(err => {
      ErrorAlert.showErrorAlert(err);
      return Promise.reject(err);
    });

  return res.data;
}

async function openPdf(filter: RegisterRalanHarianFilter): Promise<void> {
  const res = await http
    .getDataParam(`/laporan/register-ralan-harian/pdf`, {
      params: {
        tgl_start: filter.tgl_start,
        tgl_end: filter.tgl_end,
        unit: filter.unit,
        dr: filter.dr,
        asr: filter.asr,
        jenis_kunjungan: filter.jenis_kunjungan
      },
      responseType: "blob"
    })
    .catch(err => {
      ErrorAlert.showErrorAlert(err);
      return Promise.reject(err);
    });

  const url = URL.createObjectURL(
    new Blob([res.data], { type: "application/pdf" })
  );
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

async function downloadExcel(
  filter: RegisterRalanHarianFilter
): Promise<void> {
  const res = await http
    .getDataParam(`/laporan/register-ralan-harian/excel`, {
      params: {
        tgl_start: filter.tgl_start,
        tgl_end: filter.tgl_end,
        unit: filter.unit,
        dr: filter.dr,
        asr: filter.asr,
        jenis_kunjungan: filter.jenis_kunjungan
      },
      responseType: "blob"
    })
    .catch(err => {
      ErrorAlert.showErrorAlert(err);
      return Promise.reject(err);
    });

  const suffix =
    filter.tgl_start === filter.tgl_end
      ? filter.tgl_start
      : `${filter.tgl_start}_${filter.tgl_end}`;

  const url = URL.createObjectURL(
    new Blob([res.data], {
      type:
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    })
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = `register-ralan-harian_${suffix}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

export default {
  preview,
  openPdf,
  downloadExcel
};
